import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown, Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";

const fr = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "short", year: "numeric" }) : "—");
const STATUS_LABEL = { open: "Ouverte", upcoming: "À venir", closed: "Clôturée" };

export default function AdminFounders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [places, setPlaces] = useState({ total_count: "", remaining_count: "", active: true });
  const [savingP, setSavingP] = useState(false);

  useEffect(() => {
    api.get("/admin/founders-tracking").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    api.get("/founders/status").then((r) => setPlaces({ total_count: r.data.total ?? "", remaining_count: r.data.remaining ?? "", active: true })).catch(() => {});
  }, []);

  const savePlaces = async () => {
    setSavingP(true);
    try {
      const { data } = await api.put("/admin/founders-places", {
        total_count: Number(places.total_count), active: places.active,
      });
      setPlaces({ total_count: data.total_count ?? "", remaining_count: data.remaining_count ?? "", active: data.active !== false });
      toast.success("Places de l'offre mises à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSavingP(false); }
  };

  if (loading) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>;
  if (!data) return <div className="p-10 text-slate-500">Impossible de charger le suivi fondateur.</div>;

  const c = data.campaign || {};
  const n = data.counts || {};
  const rows = data.subscriptions || [];

  return (
    <div data-testid="admin-founders-page">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2">
        <Crown size={22} className="text-[#E05638]" /> Campagne Partenaire Fondateur
      </h1>
      <p className="text-sm text-slate-500 mt-1">Suivi de l'offre de lancement (0 € pendant 12 mois) et gestion des places.</p>

      {/* État campagne */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">Statut de la campagne</div>
            <div className={`mt-1 inline-flex items-center gap-2 text-lg font-extrabold ${c.status === "open" ? "text-emerald-600" : c.status === "closed" ? "text-rose-600" : "text-amber-600"}`} data-testid="founder-campaign-status">
              {c.status === "open" ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />} {STATUS_LABEL[c.status] || c.status}
            </div>
          </div>
          <div className="text-sm text-slate-600">
            <div>Début : <b>{fr(c.start)}</b></div>
            <div>Fin de commercialisation : <b>{fr(c.end)}</b></div>
            <div>Offre : <b>0 € pendant 12 mois</b> · sans carte bancaire</div>
          </div>
        </div>
      </div>

      {/* Gestion des places */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-founder-places">
        <h2 className="font-heading text-lg font-bold flex items-center gap-2"><Crown size={18} className="text-[#E05638]"/> Places de l'offre (0 € · 12 mois)</h2>
        <p className="text-xs text-slate-500 mt-1 mb-3">Gérez le nombre total de places, les places restantes affichées et l'ouverture de l'offre.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>Places au total</Label><Input type="number" value={places.total_count} onChange={(e)=>setPlaces((s)=>({ ...s, total_count: e.target.value }))} data-testid="founder-places-total"/></div>
          <div><Label>Places restantes (automatique)</Label><Input type="number" value={places.remaining_count} disabled className="bg-slate-50" data-testid="founder-places-remaining"/></div>
          <div className="flex items-end"><label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={places.active} onChange={(e)=>setPlaces((s)=>({ ...s, active: e.target.checked }))} className="w-4 h-4" data-testid="founder-places-active"/> Offre ouverte</label></div>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">Les places restantes sont calculées automatiquement : total moins le nombre de partenaires fondateurs déjà activés.</p>
        <div className="mt-3 flex justify-end"><Button onClick={savePlaces} disabled={savingP} className="btn-shine text-white" data-testid="founder-places-save">{savingP ? "…" : "Enregistrer les places"}</Button></div>
      </div>

      {/* Compteurs */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Souscriptions", n.total, "slate"], ["Actifs", n.active, "emerald"], ["Paiement en défaut", n.past_due, "amber"], ["Résiliés", n.canceled, "rose"], ["Basculés Visibilité", n.switched, "blue"]].map(([l, v, col]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{l}</div>
            <div className={`mt-1 text-2xl font-extrabold text-${col}-600`}>{v || 0}</div>
          </div>
        ))}
      </div>

      {/* Tableau garanties */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="founder-table">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-heading font-extrabold text-slate-900">
          <ShieldCheck size={18} className="text-[#E05638]" /> Partenaires fondateurs
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Aucune souscription fondateur pour le moment.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2">Partenaire</th>
                  <th className="text-left px-4 py-2">Statut</th>
                  <th className="text-left px-4 py-2">Formule</th>
                  <th className="text-left px-4 py-2">Début garantie</th>
                  <th className="text-left px-4 py-2">Fin garantie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`founder-row-${i}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-slate-900">{r.business_name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.user_email}</div>
                    </td>
                    <td className="px-4 py-2"><span className="text-xs font-semibold">{r.status || "—"}</span></td>
                    <td className="px-4 py-2">
                      {r.guarantee_switched ? <span className="text-xs font-semibold text-blue-700">Visibilité (basculé)</span> : <span className="text-xs font-semibold text-[#E05638]">Fondateur</span>}
                    </td>
                    <td className="px-4 py-2">{fr(r.guarantee_start)}</td>
                    <td className="px-4 py-2 font-semibold">{fr(r.guarantee_until)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
